import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../hooks/useApi';
import { Card, Button, Input, Select, Spinner, Badge, EmptyState, PageHeader } from '../components/ui';
import { TEAM_CODES, isAdminLike } from '@ns-b2b/shared';
import type { Visit } from '@ns-b2b/shared';

export function VisitsList() {
    const { user } = useAuth();
    const [visits, setVisits] = useState<Visit[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [total, setTotal] = useState(0);

    const [filters, setFilters] = useState({
        from: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0],
        team: '',
    });

    useEffect(() => {
        loadVisits();
    }, []);

    const loadVisits = async () => {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (filters.from) params.set('from', filters.from);
        if (filters.to) params.set('to', filters.to);
        if (filters.team) params.set('team', filters.team);

        const { data } = await api<{ data: Visit[]; total: number }>(`/api/visits?${params.toString()}`);
        if (data) {
            setVisits((data as unknown as { data: Visit[] }).data || []);
            setTotal((data as unknown as { total: number }).total || 0);
        }
        setIsLoading(false);
    };

    const handleExport = () => {
        const params = new URLSearchParams();
        if (filters.from) params.set('from', filters.from);
        if (filters.to) params.set('to', filters.to);
        if (filters.team) params.set('team', filters.team);
        window.open(`/api/export/visits.csv?${params.toString()}`, '_blank');
    };

    const canExport = user && isAdminLike(user.effectiveRole);

    return (
        <div className="space-y-6">
            <PageHeader
                title="รายการหน้างาน"
                subtitle={`ทั้งหมด ${total} รายการ`}
                action={
                    <div className="flex gap-2">
                        <Button onClick={loadVisits} variant="secondary" size="sm" icon="🔄">
                            รีเฟรช
                        </Button>
                        {canExport && (
                            <Button onClick={handleExport} variant="secondary" size="sm" icon="📥">
                                Export
                            </Button>
                        )}
                    </div>
                }
            />

            {/* Filters */}
            <Card padding="md">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Input
                        label="จากวันที่"
                        type="date"
                        value={filters.from}
                        onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                    />
                    <Input
                        label="ถึงวันที่"
                        type="date"
                        value={filters.to}
                        onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                    />
                    {user && isAdminLike(user.effectiveRole) && (
                        <Select
                            label="ทีม"
                            value={filters.team}
                            onChange={(e) => setFilters((f) => ({ ...f, team: e.target.value }))}
                            options={[
                                { value: '', label: 'ทั้งหมด' },
                                ...TEAM_CODES.map((t) => ({ value: t, label: `ทีม ${t}` })),
                            ]}
                        />
                    )}
                    <div className="flex items-end">
                        <Button onClick={loadVisits} variant="primary" className="w-full">
                            ค้นหา
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Table */}
            {isLoading ? (
                <div className="flex justify-center py-20">
                    <Spinner size="lg" />
                </div>
            ) : visits.length === 0 ? (
                <Card padding="lg">
                    <EmptyState
                        icon="📭"
                        title="ไม่พบข้อมูล"
                        description="ไม่มีรายการหน้างานในช่วงเวลาที่เลือก"
                    />
                </Card>
            ) : (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-neutral-50 border-b border-neutral-200">
                                    <th className="text-left p-4 font-semibold text-neutral-700 text-sm">วันที่</th>
                                    <th className="text-left p-4 font-semibold text-neutral-700 text-sm">ทีม</th>
                                    <th className="text-left p-4 font-semibold text-neutral-700 text-sm">ลูกค้า</th>
                                    <th className="text-left p-4 font-semibold text-neutral-700 text-sm">โทร</th>
                                    <th className="text-left p-4 font-semibold text-neutral-700 text-sm">หน้างาน</th>
                                    <th className="text-left p-4 font-semibold text-neutral-700 text-sm">บันทึกโดย</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visits.map((visit, idx) => (
                                    <tr
                                        key={visit.id}
                                        className={`border-b border-neutral-100 hover:bg-neutral-50 transition-colors
                                ${idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}`}
                                    >
                                        <td className="p-4 text-sm">{visit.date}</td>
                                        <td className="p-4">
                                            <Badge variant="primary">ทีม {visit.team}</Badge>
                                        </td>
                                        <td className="p-4 font-medium text-neutral-900">{visit.customer_name}</td>
                                        <td className="p-4">
                                            <a href={`tel:${visit.phone}`} className="text-primary hover:underline text-sm">
                                                {visit.phone}
                                            </a>
                                        </td>
                                        <td className="p-4 text-sm text-neutral-600">{visit.jobsite || '-'}</td>
                                        <td className="p-4 text-sm text-neutral-500">{visit.created_by_name || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}
